import { Check, Crown, Briefcase } from "lucide-react";
import { usd } from "@/lib/usd";

export const PLAN_FEATURES = {
  free: ["Notes with the AI scribe", "Lore Bible wiki & templates", "Quote Library", "Live co-writing & comments", "PDF, EPUB & Markdown export (Lore Bible & quotes)", "Personal & hobby use"],
  pro: ["Everything in Free", "Timelines, time-travel scrubber & calendars", "Maps with regions, travel paths & playback", "Relationship web & family trees", "Languages, glyphs & pronunciation", "Pro sections in your exports", "Unlocks Pro for everyone in your universes"],
  commercial: ["Everything in Pro", "Commercial-use license for selling your books, games and other work", "For authors who publish or plan to sell", "Same tools, honor system: no checks, no royalties"],
};

const META = {
  free: { name: "Free", icon: null, blurb: "Everything you need to start a world." },
  pro: { name: "Pro", icon: Crown, blurb: "For deep worlds with maps, history and languages." },
  commercial: { name: "Commercial", icon: Briefcase, blurb: "Pro, licensed for work you sell." },
};

export const priceFor = (prices, tier, interval) =>
  tier === "free" ? 0 : prices?.[`${tier === "commercial" ? "commercial_" : ""}${interval === "once" ? "lifetime" : interval === "year" ? "yearly" : "monthly"}`];

export const IntervalSwitch = ({ value, onChange, prices, testid = "interval-switch" }) => {
  const pct = (m, y) => (m ? Math.round((1 - y / (m * 12)) * 100) : 0);
  const save = prices ? Math.max(pct(prices.monthly, prices.yearly), pct(prices.commercial_monthly, prices.commercial_yearly)) : 0;
  return (
    <div className="inline-flex items-center rounded-full border border-[#2D3442] bg-[#0F131A] p-1 text-sm" data-testid={testid}>
      {[["month", "Monthly"], ["year", "Yearly"], ["once", "Buy once"]].map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`interval-${k}`}
          className={`rounded-full px-4 py-1.5 transition-colors ${value === k ? "bg-[#F59E0B] text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>
          {l}{k === "year" && save > 0 && <span className={`ml-1.5 text-xs ${value === k ? "text-[#0B0D10]/70" : "text-[#34D399]"}`}>save up to {save}%</span>}
        </button>
      ))}
    </div>
  );
};

export const PlanGrid = ({ prices, interval, action, current }) => (
  <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
    {["free", "pro", "commercial"].map((t) => {
      const m = META[t], Icon = m.icon, featured = t === "pro";
      const price = priceFor(prices, t, interval);
      return (
        <div key={t} data-testid={`plan-${t}-card`}
          className={`relative flex flex-col rounded-md border p-7 transition-transform duration-300 hover:-translate-y-1 ${featured ? "border-[#F59E0B]/60 bg-[#13171F] shadow-[0_0_60px_-24px_#F59E0B]" : "border-[#2D3442] bg-[#0F131A]"}`}>
          {featured && <span className="absolute -top-3 left-7 rounded-full bg-[#F59E0B] px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0B0D10]">Most popular</span>}
          {current === t && <span className="absolute -top-3 right-7 rounded-full border border-[#10B981]/50 bg-[#0B0D10] px-3 py-0.5 text-[10px] uppercase tracking-[0.16em] text-[#34D399]" data-testid={`plan-${t}-current`}>Your plan</span>}
          <div className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] ${t === "free" ? "text-[#A8A090]" : "text-[#FBBF24]"}`}>{Icon && <Icon className="h-3.5 w-3.5" />}{m.name}</div>
          <p className="mt-2 text-sm text-[#A8A090]">{m.blurb}</p>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-display text-5xl text-[#F3EBDD]" data-testid={`plan-${t}-price`}>{price == null ? "…" : usd(price)}</span>
            <span className="text-sm text-[#A8A090]">{t === "free" ? "forever" : interval === "once" ? "one time · updates free forever" : `/ ${interval === "year" ? "year" : "month"}`}</span>
          </div>
          <ul className="mt-6 flex-1 space-y-2.5 text-sm">
            {PLAN_FEATURES[t].map((f) => (
              <li key={f} className="flex items-start gap-2 text-[#D9D0C1]"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${t === "free" ? "text-[#A8A090]" : "text-[#FBBF24]"}`} />{f}</li>
            ))}
          </ul>
          <div className="mt-8">{action(t)}</div>
        </div>
      );
    })}
  </div>
);
